import { ThreadMessage } from './chatTypes';
import { ChatThreadSource } from './chatThreadSource';
import { storage } from '../../utils/storage';

/**
 * CORE-1448 — разговор с Агапеллой до регистрации.
 *
 * ## Почему это отдельный источник, а не тот же канал
 *
 * Обычный разговор идёт через SignalR-хаб `Agape.ChatApi`, и `[Authorize]` там
 * стоит **на классе хаба целиком**. Тот же хаб обслуживает личные сообщения,
 * круги и блокировки, поэтому снять с него защиту ради гостя значило бы
 * открыть заодно и их.
 *
 * Гостю хаб и не нужен. Всё, что ему положено, — один ход к модели и ответ, а
 * гостевой гейт (кука, счётчик ходов, сервисный потолок) уже живёт в
 * `awesome-chat-api` на `/api/v2/chat/stream`. Поэтому гость ходит туда
 * напрямую, а на бэкенде не снимается ни одна авторизация.
 *
 * ## Что этот источник намеренно НЕ умеет
 *
 * Ни реакций, ни правки, ни удаления, ни «печатает». Всё это требует канала и
 * чужих сообщений, а у гостя ни того, ни другого нет: разговор живёт в памяти
 * вкладки и никуда не записывается. История гостя в аккаунт не переносится —
 * решение продукта от 21.09.
 *
 * Вложений тоже нет. Одно фото стоит дороже всех пяти текстовых ходов гостя
 * (2304 входных токена против бюджета в 9000 на весь ход), и показывать
 * Агапелле свою жизнь до регистрации незачем.
 */

/**
 * Заголовок с идентификатором гостя. Сервер присылает его в ответе, мы кладём
 * в хранилище первой стороны и возвращаем на следующем ходу.
 *
 * Куку сервер тоже ставит, и для нативного клиента она и работает. Но в вебе
 * она сторонняя: Safari её не отдаёт, Chrome и Яндекс.Браузер — в инкогнито.
 * Без заголовка каждый ход приходил без идентификатора, сервер заводил нового
 * гостя, и лимит не наступал никогда (возврат QA 03.10).
 */
const GUEST_ID_HEADER = 'X-Guest-Id';

const GUEST_ENDPOINT = '/api/v2/chat/stream';
/** Вводная сценария знакомства — та же, что слышит вошедший. */
const GREETING_ENDPOINT = '/api/v2/chat/guest/greeting';

export interface GuestChatSourceOptions {
  /** База `awesome-chat-api` без завершающего слэша. */
  baseUrl: string;
  /** Запасная первая реплика: показывается, если сервер не ответил. */
  greeting: string;
  /** Что сказать, если сеть не ответила. */
  offlineText?: string;
}

const AGAPELLA = 'Агапелла';

function mine(text: string): ThreadMessage {
  return {
    id: `guest-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text,
    isMine: true,
    createdAt: new Date(),
    read: true,
    senderName: '',
    senderAvatar: null,
    attachments: [],
    reactions: [],
    myReactions: [],
    pinned: false,
    quoted: null,
    aiGenerated: false,
    quickReplies: [],
  };
}

function hers(text: string): ThreadMessage {
  return {
    id: `agapella-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text,
    isMine: false,
    createdAt: new Date(),
    read: true,
    senderName: AGAPELLA,
    senderAvatar: null,
    attachments: [],
    reactions: [],
    myReactions: [],
    pinned: false,
    quoted: null,
    aiGenerated: true,
    quickReplies: [],
  };
}

export class GuestChatSource implements ChatThreadSource {
  private messages: ThreadMessage[] = [];
  private listener: (() => void) | null = null;
  private thinking = false;
  private aborted = false;

  constructor(private readonly options: GuestChatSourceOptions) {}

  async connect(): Promise<void> {
    // Подключаться некуда: ни сокета, ни канала. Модель для приветствия не
    // зовётся — платить за «привет» из бюджета, ради ограничения которого всё
    // и делается, было бы странно.
    //
    // Но и хранить текст здесь нельзя: гость обязан слышать ту же вводную
    // сценария знакомства, что и вошедший (решение продукта 28.09), а она
    // живёт в чат-сервисе. Копия в этом репозитории разошлась бы с оригиналом
    // на первой же правке продукта — тихо, без единой ошибки.
    //
    // Константа остаётся запасным вариантом на случай молчания сети: пустой
    // экран вместо разговора хуже, чем чуть более старая фраза.
    this.messages = (await this.fetchIntro()).map(hers);
    this.emit();
  }

  /**
   * Знакомство с платформой — несколько сообщений подряд, а не одно.
   *
   * Два хода — решение продукта от 28.09 о том, как человек это читает.
   * Склеить их в одну простыню значило бы тихо его отменить, поэтому здесь
   * список, а не строка.
   *
   * Ходы гостя на это не тратятся: текст статичный, модель для него не
   * зовётся. Лимит ограничивает платные обращения к модели, а не количество
   * сообщений на экране.
   */
  /**
   * Заголовки запроса с идентификатором гостя, если он у нас есть.
   *
   * Отдельным методом, а не инлайном в каждом запросе: мест два (знакомство и
   * ход), и разъехаться им нельзя — тогда знакомство и разговор оказались бы
   * разными гостями, и воронка перестала бы сходиться.
   */
  private async guestHeaders(): Promise<Record<string, string>> {
    try {
      const id = await storage.getGuestChatId();
      return id ? { [GUEST_ID_HEADER]: id } : {};
    } catch {
      // Приватный режим может запрещать доступ к хранилищу. Без
      // идентификатора ход всё равно состоится — это хуже для лимита, но
      // ронять из-за него весь разговор нельзя.
      return {};
    }
  }

  /** Запомнить идентификатор, который вернул сервер. */
  private async rememberGuestId(response: Response): Promise<void> {
    try {
      const id = response.headers?.get?.(GUEST_ID_HEADER);
      if (id) await storage.setGuestChatId(id);
    } catch {
      // см. выше
    }
  }

  private async fetchIntro(): Promise<string[]> {
    try {
      const response = await fetch(`${this.options.baseUrl}${GREETING_ENDPOINT}`, {
        method: 'GET',
        headers: { Accept: 'application/json', ...(await this.guestHeaders()) },
        credentials: 'include',
      });
      await this.rememberGuestId(response);
      if (!response.ok) return [this.options.greeting];
      const body = await response.json();

      const steps = Array.isArray(body?.steps)
        ? body.steps.filter((s: unknown): s is string => typeof s === 'string' && s.trim() !== '')
        : [];
      if (steps.length > 0) return steps;

      // Сервер постарше знает только про одну фразу — это не ошибка.
      const single = typeof body?.greeting === 'string' ? body.greeting.trim() : '';
      return [single || this.options.greeting];
    } catch {
      return [this.options.greeting];
    }
  }

  disconnect(): void {
    this.aborted = true;
    this.listener = null;
  }

  onChange(cb: () => void): void {
    this.listener = cb;
  }

  getMessages(): ThreadMessage[] {
    return this.messages;
  }

  isThinking(): boolean {
    return this.thinking;
  }

  markRead(): void {
    // Читать нечего: собеседник один и он отвечает сразу.
  }

  async send(text: string): Promise<void> {
    const body = text.trim();
    if (!body || this.thinking) return;

    this.messages = [...this.messages, mine(body)];
    this.thinking = true;
    this.emit();

    const reply = hers('');
    let appended = false;

    try {
      const response = await fetch(`${this.options.baseUrl}${GUEST_ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await this.guestHeaders()) },
        // Без этого браузер не пошлёт куку гостя: чат-сервис живёт на другом
        // домене, и без неё каждый ход считался бы новым гостем.
        credentials: 'include',
        // `user_id` пустой намеренно — по нему сервер и понимает, что это
        // гость. Заполненный означал бы вход, и гостевой лимит не применился
        // бы вовсе.
        //
        // `companion_id` не шлётся СОВСЕМ. Здесь стояло `'auto'` — значение,
        // которое сервис отдаёт в ответе, когда собеседника выбрал он сам, но
        // на входе такого id не существует: каждое сообщение гостя получало
        // 404 и стоило ему хода. Собеседника гостю выбирает сервер, и знать
        // его имя клиенту незачем.
        body: JSON.stringify({ user_id: '', message: body }),
      });

      // Запоминаем ДО проверки кода: отказ по исчерпанному лимиту — тоже
      // ответ гостю, и потерять идентификатор на нём значит выдать человеку
      // новую полную квоту следующим запросом.
      await this.rememberGuestId(response);

      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done || this.aborted) break;
        buffer += decoder.decode(value, { stream: true });

        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          const delta = readDelta(frame);
          if (delta == null) continue;
          reply.text += delta;
          if (!appended) {
            this.messages = [...this.messages, reply];
            appended = true;
          } else {
            this.messages = [...this.messages];
          }
          this.emit();
        }
      }
    } catch {
      // Молча ронять ход нельзя: человек останется смотреть на свою реплику и
      // решит, что платформа сломана.
      if (!appended) {
        reply.text = this.options.offlineText
          ?? 'Что-то с соединением — попробуй, пожалуйста, ещё раз ✦';
        this.messages = [...this.messages, reply];
      }
    } finally {
      this.thinking = false;
      this.emit();
    }
  }

  private emit(): void {
    this.listener?.();
  }
}

/**
 * Достать текст из одного SSE-кадра. `null` — кадр служебный (`[DONE]`,
 * `event: usage`) или пустой.
 */
export function readDelta(frame: string): string | null {
  const line = frame.split('\n').find((l) => l.startsWith('data: '));
  if (!line) return null;
  const payload = line.slice('data: '.length);
  if (payload === '[DONE]' || payload === '') return null;
  // Кадр учёта расхода приходит объектом и в разговор не попадает.
  if (payload.startsWith('{')) return null;
  return payload;
}
